import { useActivatePayoutAccountMutation } from '@/hooks/payout-account/mutations/use-activate-payout-account.mutation';
import { useModal } from '@/hooks/shared/use-modal';
import { useUserQuery } from '@/hooks/user/queries/use-user.query';
import PayoutMethodIcon from '@/partials/group/settings/payout/PayoutMethodIcon';
import { PayoutAccount } from '@/types/api/payout-account';
import { formatMobileNumber } from '@/utils/shared/formatters';

export interface ActivatePayoutAccountModalProps {
  account: PayoutAccount;
}

export default function ActivatePayoutAccountModal({ account }: ActivatePayoutAccountModalProps) {
  const mutation = useActivatePayoutAccountMutation();
  const { close } = useModal();
  const { data: user } = useUserQuery(account.userId);

  return (
    <div className="flex flex-col gap-4 ">
      {/* Icon */}
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-gray-100 dark:bg-gray-700">
        <svg className="shrink-0 fill-current text-red-500" width="16" height="16" viewBox="0 0 16 16">
          <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
        </svg>
      </div>

      {/* Content */}
      <div className="w-full">
        {/* Modal header */}
        <div className="mb-3">
          <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">Byt utbetalningskonto?</div>
        </div>

        {/* Modal content */}
        <div className="text-sm mb-12">
          <div className="space-y-2">
            <p>Alla kommande utbetalningar kommer göras till nya konto med omedelbar verkan.</p>
            <p>Bytet sker till:</p>
            <div className="w-full h-full text-left py-3 px-4 rounded-lg bg-white dark:bg-gray-800 border-2 border-violet-400 dark:border-violet-500 shadow-sm transition">
              <div className="flex items-center space-x-3">
                <PayoutMethodIcon payoutMethod={account.payoutMethod} />
                <div className="grow">
                  <div className="flex flex-wrap items-center justify-between mb-0.5">
                    <span className="font-medium text-gray-800 dark:text-gray-100">
                      {formatMobileNumber(account.accountName) ?? account.accountName}{' '}
                    </span>
                  </div>
                  <div className="text-sm">
                    {user?.firstName} {user?.lastName}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal footer */}
        <div className="flex flex-wrap justify-end space-x-2">
          <button
            className="btn-sm border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
          >
            Avbryt
          </button>
          <button
            className="btn-sm bg-red-500 hover:bg-red-600 text-white"
            onClick={() =>
              mutation.mutate({ payoutAccountId: account.id, groupId: account.groupId }, { onSuccess: () => close() })
            }
          >
            Ja, byt konto
          </button>
        </div>
      </div>
    </div>
  );
}
